import { Button, Layout, MenuProps, Typography, notification } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DeleteButton from 'src/app/shared/components/common/button/delete-button'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import ActionConfirmationModal from 'src/app/shared/components/modal/action-confirmation-modal'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { bulkDeleteAccountApi, deleteAccountApi, getFilteredAccounts } from 'src/app/shared/services/ClientSetupService'
import { getAccountsDownloadFile, getAccountsDownloadStatus } from 'src/app/shared/services/AccountService'
import { colorText, error } from 'src/app/themes/primaryThemeDefault'
import { DeleteIcon, EyeIcon, PenIcon, UserIcon } from 'src/assets/icons'
import DeleteImg from 'src/assets/images/delete-img.png'
import { useAccountTab } from './useAccountTab'

/* Localization TimeZone */
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { YesNoTag } from 'src/app/features/feature-business-administration/bank-user-setup/components/TagComponents'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useAccountSetupFilters } from '../AccountSetupFiltersContext'
dayjs.extend(utc)
dayjs.extend(timezone)
/* Localization TimeZone */

const AccountTab: React.FC<{ onUpload: () => any; resetView: boolean }> = ({ onUpload, resetView }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userPermissions, role } = useRolePermission()
  const { accountList, getAccountList, isDeleting, setIsDeleting, isGettingList } = useAccountTab()
  const [isModalConfirmDeleteOpened, setIsModalConfirmDeleteOpened] = useState<boolean>(false)
  const [selectedAccount, setSelectedAccount] = useState<any>({})
  const [selectedRows, setSelectedRows] = useState<Array<any>>([])
  const [isConfirmBulkDeletionModalOpened, setIsConfirmBulkDeletionModalOpened] = useState<boolean>(false)
  const [api, contextHolder] = notification.useNotification()
  const { saveSearchParams, restoreSearchParams } = useAccountSetupFilters()

  // Initialize defaultParams from sessionStorage
  const [defaultParams] = useState<any>(() => {
    const savedParams = restoreSearchParams()
    return savedParams || {}
  })

  const getActionItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>{t('AccountSetup:actions.view_account')}</Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.accountSetup}/view/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>{t('AccountSetup:actions.edit_account')}</Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.accountSetup}/edit/${rowData?.id}`)
              },
            }
          : null,
        {
          key: '3',
          label: (
            <Typography.Text style={{ fontSize: '14px', color: colorText }}>
              {t('AccountSetup:actions.view_users_for_account')}
            </Typography.Text>
          ),
          icon: <UserIcon />,
          onClick: () => {
            if (rowData?.id) navigate(`${menuUrls.viewUsersForAccount}/${rowData?.id}`)
          },
        },
        userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.DELETE_ITEMS)
          ? {
              key: '4',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: error }}>
                  {t('AccountSetup:actions.delete_account')}
                </Typography.Text>
              ),
              icon: <DeleteIcon />,
              onClick: () => {
                if (rowData?.id) {
                  setSelectedAccount(rowData)
                  setIsModalConfirmDeleteOpened(true)
                }
              },
            }
          : null,
      ]
    },
    [userPermissions, t, navigate],
  )

  const getColumnsToSetEmpty = (serviceTypeParameter?: string): string[] => {
    const normalizedServiceType = String(serviceTypeParameter ?? '')
      .trim()
      .toUpperCase()
  
    if (['ACH ONLY'].includes(normalizedServiceType)) {
      return ['defaultCheckDecision', 'defaultCheckReason', 'checkExceptionProcessingType']
    }
  
    if (['CHECK ONLY'].includes(normalizedServiceType)) {
      return ['defaultAchDecision', 'defaultAchReason', 'achExceptionProcessingType']
    }
  
    return []
  }

  const generateCustomColumnDefs = useCallback((columnDefs: TableColumnDefs[]) => {
    const columnsWithConditionalEmptyValue = [
      'defaultCheckDecision',
      'defaultCheckReason',
      'defaultAchDecision',
      'defaultAchReason',
      'checkExceptionProcessingType',
      'achExceptionProcessingType',
    ]

    const defs = columnDefs.map((item) => {
      if (item.colId === 'payeeMatch') {
        item.dataType = 'date'
        item.cellRenderer = (row: any) => {
          return <YesNoTag status={row.data.payeeMatch} />
        }
      }

      if (['dateCreated', 'archived'].includes(item.colId)) {
        item.dataType = 'text'
        item.valueFormatter = (param: any) => param?.value
        // ? convertTime({
        //     date: param?.value,
        //   })
        // : ''
      }

      if (['accountNumber', 'staleDatedDays'].includes(item.colId)) {
        item.dataType = 'number'
      }

      if (columnsWithConditionalEmptyValue.includes(item.colId)) {
        item.dataType = 'text'
        item.valueGetter = (params: any) => {
          const columnsToSetEmpty = getColumnsToSetEmpty(params?.data?.serviceTypeParameter)

          if (columnsToSetEmpty.includes(item.colId)) {
            return ''
          }

          return params?.data?.[item.colId]
        }
        item.valueFormatter = (params: any) => params?.value ?? ''
      }

      return item
    })
    return defs
  }, [])

  const handleSearch = useCallback(
    async (value: Partial<IGetExportData>) => {
      const paramsToSave = {
        ...value,
        isSearchAllColumns: (value.searchColumns?.length || 0) > 1,
      }
      saveSearchParams(paramsToSave)
      await getAccountList(value)
    },
    [getAccountList],
  )

  const handleDeleteAccount = useCallback(async () => {
    try {
      setIsDeleting(true)
      if (selectedAccount?.id) {
        await deleteAccountApi(selectedAccount.id)
        await outerTableRef.current?.handleSearchLogic?.({})
        api.success({
          message: '',
          description: (
            <RichTextField richText={t('common:delete_record_success', { record_name: selectedAccount.accountName })} />
          ),
        })
      }
    } catch (error: any) {
      api.error({
        message: error?.message ?? t('common:error'),
      })
    } finally {
      setIsDeleting(false)
      setIsModalConfirmDeleteOpened(false)
    }
  }, [api, selectedAccount.accountName, selectedAccount.id, setIsDeleting, t])

  useEffect(() => {
    if (resetView) {
      handleSearch({
        pageIndex: 1,
        pageSize: 50,
        sortColumnsDictionary: { clientName: 'asc', accountName: 'asc' },
      })
    }
  }, [resetView])

  const handleBulkDeleteRecord = useCallback(async () => {
    try {
      setIsDeleting(true)
      if (selectedRows && selectedRows.length > 0) {
        const ids = selectedRows.map((row: any) => row.id)
        await bulkDeleteAccountApi(ids)
        await outerTableRef.current?.handleSearchLogic?.({})
        api.success({
          message: '',
          description:
            t('common:bulk_delete:success_message', {
              count: selectedRows?.length ?? 0,
              type: 'account',
            }) || '',
        })
      }
    } catch (error: any) {
      api.error({
        message: error?.message ?? t('common:error'),
      })
    } finally {
      setIsDeleting(false)
      setSelectedRows([])
      setIsConfirmBulkDeletionModalOpened(false)
    }
  }, [api, setIsDeleting, t, selectedRows])

  const confirmAccountBulkDeletion = useCallback(() => {
    if (outerTableRef.current?.getSelectedRows) {
      const items = outerTableRef.current?.getSelectedRows() ?? []
      setSelectedRows([...items])
      setIsConfirmBulkDeletionModalOpened(true)
    }
  }, [])

  // Restore saved search params when component mounts
  useEffect(() => {
    const savedParams = restoreSearchParams()
    
    if (savedParams) {
      const { isSearchAllColumns, searchTerm } = savedParams
      setTimeout(() => {
        const formSearchColumns = isSearchAllColumns ? 'All' : (savedParams.searchColumns?.[0] || 'All')
        
        outerTableRef.current?.setSearchFormValues?.(
          searchTerm || '',
          formSearchColumns
        )
        if (searchTerm) {
          outerTableRef.current?.setSearchOpen?.(true)
        }
      }, 300)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout.Content>
      {contextHolder}
      <NewCommonTable
        ref={outerTableRef}
        rowData={accountList?.items}
        total={accountList?.total}
        displayAddBtn={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
        displayBulkDeleteBtn
        displayUploadButton={
          role === 'Bank User' && userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
        }
        displayDownloadBtn={
          userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.VIEW_ITEMS) ||
          userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
        }
        displayActionColumn
        pageName={TABLE_NAME.ACCOUNTS}
        tableSettingName={TABLE_NAME.ACCOUNTS}
        translationNamespace='AccountSetup:account_list'
        loading={isGettingList}
        defaultParams={defaultParams}
        handleSearch={handleSearch}
        handleDownload={async (payload) => await getFilteredAccounts(payload)}
        getDownloadStatus={getAccountsDownloadStatus}
        getDownloadFile={getAccountsDownloadFile}
        actionDropdownItems={getActionItems}
        handleAddRecord={() => navigate(`${menuUrls.accountSetup}/add`)}
        generateCustomColumnDefs={generateCustomColumnDefs}
        handleUpload={onUpload}
        settingHeightTable={settingHeightTableWithFilter}
        handleBulkDeleteRecord={confirmAccountBulkDeletion}
      />

      <ActionConfirmationModal
        icon={<img src={DeleteImg} alt='Confirm Icon' />}
        title={t('Modal:delete_record_title')}
        isOpen={isModalConfirmDeleteOpened}
        description={
          <RichTextField
            richText={
              t('AccountSetup:actions.delete_account_confirm_description', {
                accountName: selectedAccount?.accountName,
              }) || ''
            }
          />
        }
        descriptionType='string'
        onClose={() => {
          setIsModalConfirmDeleteOpened(false)
        }}
        footer={[
          <Button
            size='middle'
            type='primary'
            key='btn-cancel'
            onClick={() => {
              setIsModalConfirmDeleteOpened(false)
            }}
          >
            {t('common:cancel')}
          </Button>,
          <DeleteButton key='btn-delete' loading={isDeleting} disabled={isDeleting} onClick={handleDeleteAccount}>
            {t('common:delete')}
          </DeleteButton>,
        ]}
      />
      <ActionConfirmationModal
        icon={<img src={DeleteImg} alt='Confirm Icon' />}
        title={t('common:bulk_delete:record_title')}
        isOpen={isConfirmBulkDeletionModalOpened}
        description={
          <RichTextField
            richText={
              t('common:bulk_delete:confirm_description', {
                count: selectedRows?.length ?? 0,
              }) || ''
            }
          />
        }
        descriptionType='string'
        onClose={() => {
          setSelectedRows([])
          setIsConfirmBulkDeletionModalOpened(false)
        }}
        footer={[
          <Button
            size='middle'
            type='primary'
            key='btn-cancel'
            onClick={() => {
              setSelectedRows([])
              setIsConfirmBulkDeletionModalOpened(false)
            }}
          >
            {t('common:cancel')}
          </Button>,
          <DeleteButton key='btn-delete' loading={isDeleting} disabled={isDeleting} onClick={handleBulkDeleteRecord}>
            {t('common:delete')}
          </DeleteButton>,
        ]}
      />
    </Layout.Content>
  )
}

export default AccountTab
